import { useState } from 'react';

import { Add } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Box,
  InputAdornment,
  Typography,
  Chip,
  Paper,
  Tooltip,
} from '@mui/material';
import { Modal } from '@scorenco/components';
import {
  SportType,
  Types,
  useTranslation,
  useUpdateChallengeRuleOne,
} from '@scorenco/core';
import { useFormik } from 'formik';
import { Reorder, useDragControls } from 'framer-motion';
import { useSnackbar } from 'notistack';

import { T_KEYS } from '@/translations';

export const EditRulesModal = ({
  rule,
  closeModal,
  sportId,
}: {
  rule: Types.Tournaments_Challenge_Rule;
  closeModal: () => void;
  sportId: number;
}) => {
  const ruleMutation = useUpdateChallengeRuleOne();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const RANKING_RULES = [
    {
      rule: 1,
      label: t(T_KEYS.TOURNAMENTS_RANKING_RULES_1),
    },
    {
      rule: 2,
      label: t(T_KEYS.TOURNAMENTS_RANKING_RULES_2),
    },
    {
      rule: 3,
      label: t(T_KEYS.TOURNAMENTS_RANKING_RULES_3),
    },
    {
      rule: 4,
      label: t(T_KEYS.TOURNAMENTS_RANKING_RULES_4),
    },
    {
      rule: 5,
      label: t(T_KEYS.TOURNAMENTS_RANKING_RULES_5),
    },
    {
      rule: 6,
      label: t(T_KEYS.TOURNAMENTS_RANKING_RULES_6),
    },
  ];

  const formik = useFormik({
    initialValues: {
      name: rule.name,
      rank: rule.rank,
      nb_groups: rule.nb_groups,
      nb_teams: rule.nb_teams,
      nb_teams_in_final_phase: rule.nb_teams_in_final_phase,
      period_duration: rule.period_duration,
      rule_type: rule.rule_type,
      time_between_periods: rule.time_between_periods,
      period_nb: rule.period_nb,
      win_point: rule?.ranking_point?.win ?? 3,
      draw_point: rule?.ranking_point?.even ?? 2,
      lost_point: rule?.ranking_point?.lost ?? 1,
      ranking_rules: [...rule.ranking_rules]?.sort(
        (a, b) => a.position - b.position
      ),
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await ruleMutation.execute({
          ruleId: rule.id,
          rule: {
            name: values.name,
            rank: values.rank,
            nb_groups: values.nb_groups,
            nb_teams: values.nb_teams,
            nb_teams_in_final_phase: values.nb_teams_in_final_phase,
            period_duration: values.period_duration,
            rule_type: values.rule_type,
            time_between_periods: values.time_between_periods,
            period_nb: values.period_nb,
          },
          rankingPoints: {
            win: values.win_point,
            even: values.draw_point,
            lost: values.lost_point,
          },
          rankingRules: values.ranking_rules.map((ranking_rule, idx) => ({
            challenge_rule_id: rule.id,
            position: idx + 1,
            rule: ranking_rule.rule,
          })),
        });
        enqueueSnackbar(t(T_KEYS.SUCCESS_EDIT), {
          variant: 'success',
        });
        closeModal();
        formik.resetForm();
      } catch (error) {
        console.error('error: ', error);
        enqueueSnackbar(t(T_KEYS.ERROR_MESSAGE), {
          variant: 'error',
        });
      }
    },
  });

  return (
    <Modal
      sx={{ mt: 2 }}
      closeModal={closeModal}
      title={t(T_KEYS.TOURNAMENTS_CHALLENGE_EDIT_PHASE)}
    >
      <Box component="form" name="editnews" onSubmit={formik.handleSubmit}>
        <Box sx={{ pt: 2, pb: 2, pl: 4, pr: 4 }}>
          <DialogContent
            style={{
              paddingTop: '1rem',
            }}
          >
            <Stack
              sx={{
                width: '100%',
              }}
              spacing={2}
            >
              <Stack
                direction="row"
                sx={{
                  width: '100%',
                  alignItems: 'center',
                }}
                spacing={2}
              >
                <TextField
                  label={t(T_KEYS.TOURNAMENTS_RULES_NAME_LABEL)}
                  value={formik.values.name}
                  name="rules_rule_name"
                  id="rules_rule_name"
                  onChange={(event) =>
                    formik.setFieldValue('name', event.target.value)
                  }
                  fullWidth
                />
              </Stack>

              <Stack
                direction="row"
                sx={{
                  width: '100%',
                }}
                spacing={2}
              >
                {sportId !== SportType.Volley && (
                  <TextField
                    label={t(T_KEYS.TOURNAMENTS_RULES_PERIODTIME_LABEL)}
                    value={formik.values.period_duration}
                    name="rules_rule_period_duration"
                    id="rules_rule_period_duration"
                    onChange={(event) =>
                      formik.setFieldValue(
                        'period_duration',
                        event.target.value
                      )
                    }
                    InputProps={{
                      inputProps: { min: 1 },
                      endAdornment: (
                        <InputAdornment position="end">
                          {formik.values.period_duration > 1
                            ? t(
                                T_KEYS.TOURNAMENTS_RULES_PERIODTIME_ADORNMENT_PLURIAL
                              )
                            : t(
                                T_KEYS.TOURNAMENTS_RULES_PERIODTIME_ADORNMENT_SINGLE
                              )}
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    type="number"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                <TextField
                  label={
                    sportId === SportType.Volley
                      ? 'Nombre de sets gagnants'
                      : 'Nombre de périodes'
                  }
                  value={formik.values.period_nb}
                  name="rules_rule_period_nb"
                  id="rules_rule_period_nb"
                  onChange={(event) =>
                    formik.setFieldValue(`period_nb`, event.target.value)
                  }
                  InputProps={{
                    inputProps: { min: 1 },
                    endAdornment: (
                      <InputAdornment position="end">
                        {sportId === SportType.Volley
                          ? formik.values.period_nb > 1
                            ? 'sets'
                            : 'set'
                          : formik.values.period_nb > 1
                            ? 'périodes'
                            : 'période'}
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  type="number"
                />
                {formik.values.period_nb > 1 &&
                  sportId !== SportType.Volley && (
                    <TextField
                      label={t(
                        T_KEYS.TOURNAMENTS_RULES_TIMEBETWEENPERIODS_LABEL
                      )}
                      value={formik.values.time_between_periods}
                      name="rules_rule_time_between_periods"
                      id="rules_rule_time_between_periods"
                      onChange={(event) =>
                        formik.setFieldValue(
                          'time_between_periods',
                          event.target.value
                        )
                      }
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        inputProps: { min: 1 },
                        endAdornment: (
                          <InputAdornment position="end">
                            {formik.values.time_between_periods > 1
                              ? t(
                                  T_KEYS.TOURNAMENTS_RULES_PERIODTIME_ADORNMENT_PLURIAL
                                )
                              : t(
                                  T_KEYS.TOURNAMENTS_RULES_PERIODTIME_ADORNMENT_SINGLE
                                )}
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                      type="number"
                    />
                  )}
              </Stack>
              <Typography variant="h5">
                Calcul des points pour le classement
              </Typography>
              <Stack
                direction="row"
                sx={{
                  width: '100%',
                }}
                spacing={2}
              >
                <TextField
                  label="Victoire"
                  value={formik.values?.win_point}
                  name="win_point"
                  id="win_point"
                  onChange={formik.handleChange}
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                  fullWidth
                  type="number"
                />
                <TextField
                  label="Match nul"
                  value={formik.values?.draw_point}
                  name="draw_point"
                  id="draw_point"
                  onChange={formik.handleChange}
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                  fullWidth
                  type="number"
                />
                <TextField
                  label="Défaite"
                  value={formik.values?.lost_point}
                  name="lost_point"
                  id="lost_point"
                  onChange={formik.handleChange}
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                  fullWidth
                  type="number"
                />
              </Stack>
              <Typography variant="h5">Règles de classement</Typography>
              <Typography variant="caption">
                Sélectionnez et triez les règles de classement pour départager
                les équipes à égalité de points
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  display: 'flex',
                  gap: 2,
                  flexDirection: 'column',
                }}
              >
                <Reorder.Group
                  axis="y"
                  onReorder={(value) => {
                    formik.setFieldValue(
                      'ranking_rules',
                      value.map((rule) => ({
                        ...rule,
                      }))
                    );
                  }}
                  values={formik.values.ranking_rules}
                  style={{
                    width: '100%',
                    padding: 0,
                    display: 'flex',
                    gap: 4,
                    flexDirection: 'column',
                  }}
                >
                  {formik.values.ranking_rules.map((ranking_rule) => (
                    <Item
                      key={ranking_rule.position}
                      ranking_rule={ranking_rule}
                      formik={formik}
                      RANKING_RULES={RANKING_RULES}
                    />
                  ))}
                </Reorder.Group>
              </Paper>
              <Box>
                {RANKING_RULES.filter(
                  (ranking_rule) =>
                    !formik.values.ranking_rules.find(
                      (rule) => rule.rule === ranking_rule.rule
                    )
                ).map((ranking_rule, idx) => (
                  <Chip
                    key={idx}
                    label={ranking_rule.label}
                    onDelete={() => {
                      formik.setFieldValue('ranking_rules', [
                        ...formik.values.ranking_rules,
                        {
                          position: formik.values.ranking_rules.length + 1,
                          rule: ranking_rule.rule,
                        },
                      ]);
                    }}
                    deleteIcon={<Add />}
                    sx={{
                      height: 'auto',
                      minHeight: '2rem',
                      marginBottom: '1rem',
                      '& .MuiChip-label': {
                        display: 'block',
                        whiteSpace: 'normal',
                      },
                    }}
                  />
                ))}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ m: 0, p: 1, justifyContent: 'center' }}>
            <LoadingButton
              type="submit"
              variant="contained"
              color="secondary"
              loading={ruleMutation.status.loading}
            >
              {t(T_KEYS.EDIT)}
            </LoadingButton>
          </DialogActions>
        </Box>
      </Box>
    </Modal>
  );
};

const Item = ({ ranking_rule, formik, RANKING_RULES }) => {
  const dragControls = useDragControls();
  const [help, setHelp] = useState("Glissez-déposez pour changer l'ordre");

  return (
    <Reorder.Item
      value={ranking_rule}
      id={ranking_rule.position}
      dragListener={false}
      dragControls={dragControls}
      style={{ listStyle: 'none' }}
      onDrag={() => {
        setHelp(`Relâchez pour déplacer à la position
        ${
          formik.values.ranking_rules?.findIndex(
            (rule) => rule.rule === ranking_rule.rule
          ) + 1
        }`);
      }}
      onDragEnd={() => {
        setHelp("Glissez-déposez pour changer l'ordre");
      }}
    >
      <Tooltip
        title={help}
        arrow
        disableInteractive
        key={formik.values.ranking_rules?.findIndex(
          (rule) => rule.rule === ranking_rule.rule
        )}
      >
        <Chip
          key={ranking_rule.position}
          onPointerDown={(event) => dragControls.start(event)}
          onClick={(event) => {
            event.preventDefault();
          }}
          label={RANKING_RULES.find((r) => r.rule === ranking_rule.rule)?.label}
          variant="outlined"
          onDelete={() => {
            formik.setFieldValue(
              'ranking_rules',
              formik.values.ranking_rules
                .filter((rule) => rule.position !== ranking_rule.position)
                .map((rule, idx) => ({
                  ...rule,
                  position: idx + 1,
                }))
            );
          }}
          sx={{
            cursor: 'grab',
            height: 'auto',
            minHeight: '2rem',
            '& .MuiChip-label': {
              display: 'block',
              whiteSpace: 'normal',
            },
          }}
        />
      </Tooltip>
    </Reorder.Item>
  );
};
